import { useEffect, useState } from 'react'
import type { SolicitudCambioResponse } from '../services/jefeCocina'

interface RevertirF11BannerProps {
  decision: SolicitudCambioResponse
  ventanaMinutos: number
  onRevertir: (id: number) => Promise<void>
  onDismiss: () => void
}

export default function RevertirF11Banner({
  decision,
  ventanaMinutos,
  onRevertir,
  onDismiss,
}: RevertirF11BannerProps) {
  const decisionAtMs = decision.chef_decision_at
    ? new Date(decision.chef_decision_at).getTime()
    : Date.now()
  const ventanaMs = ventanaMinutos * 60_000

  const [segundosRestantes, setSegundosRestantes] = useState<number>(() => {
    const ms = decisionAtMs + ventanaMs - Date.now()
    return Math.max(0, Math.floor(ms / 1000))
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!decision.puede_revertir) return
    const id = window.setInterval(() => {
      const ms = decisionAtMs + ventanaMs - Date.now()
      const secs = Math.max(0, Math.floor(ms / 1000))
      setSegundosRestantes(secs)
      if (secs <= 0) {
        window.clearInterval(id)
        // Banner se auto-cierra al expirar.
        onDismiss()
      }
    }, 1000)
    return () => window.clearInterval(id)
  }, [decisionAtMs, ventanaMs, decision.puede_revertir, onDismiss])

  if (!decision.puede_revertir || segundosRestantes <= 0) {
    return null
  }

  const accionLabel = decision.estado === 'APROBADA' ? 'Aprobada' : 'Rechazada'
  const mins = Math.floor(segundosRestantes / 60)
  const secs = segundosRestantes % 60
  const cuenta = `${mins}:${secs.toString().padStart(2, '0')}`

  async function handleRevertir() {
    setSubmitting(true)
    setError(null)
    try {
      await onRevertir(decision.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo revertir')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      role="status"
      className="bg-avante-warning/90 text-black border-l-4 border-avante-warning px-4 py-2 flex flex-wrap items-center justify-between gap-2 sticky top-[64px] z-20"
    >
      <span className="text-sm font-semibold">
        Solicitud #{decision.id} {accionLabel}. Podes revertir por {cuenta}.
      </span>
      <div className="flex items-center gap-2">
        {error && <span className="text-xs text-red-800">{error}</span>}
        <button
          type="button"
          onClick={handleRevertir}
          disabled={submitting}
          className="bg-black text-white px-3 py-1 rounded text-sm font-semibold disabled:opacity-50"
        >
          {submitting ? 'Revirtiendo…' : 'Revertir'}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Cerrar banner"
          className="text-sm underline"
        >
          Cerrar
        </button>
      </div>
    </div>
  )
}
